import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, CheckCircle2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { SEO, breadcrumbJsonLd, faqJsonLd } from "../components/SEO";
import { Layout, Breadcrumbs } from "../components/Layout";
import { CountrySelect } from "../components/CountrySelect";
import { PrivacyConsent } from "../components/PrivacyConsent";
import { COUNTRIES, dialFor, withDial } from "../lib/countries";
import { officeFor } from "../lib/regions";
import { loadGoogleMaps } from "../lib/googleMaps";
import { lp } from "../lib/urls";

const inputCls =
  "w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-gray-950 transition-colors";
const labelCls =
  "block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5";

export default function RequestQuote() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    address: "",
    message: "",
  });
  const [parts, setParts] = useState([
    {
      brand: params.get("brand") || "",
      model: params.get("model") || "",
      reference: "",
      reference_brand: "",
      serial: "",
      qty: "",
      description: "",
      photo: "",
    },
  ]);
  const setPart = (i, k) => (e) =>
    setParts((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: e.target.value } : p)));
  const addPart = () =>
    setParts((ps) => [...ps, { brand: "", model: "", reference: "", reference_brand: "", serial: "", qty: "", description: "", photo: "" }]);
  const removePart = (i) => setParts((ps) => ps.filter((_, j) => j !== i));
  const uploadIdxRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showForm, setShowForm] = useState(
    Boolean(params.get("brand") || params.get("model") || params.get("parts")),
  );
  const fileRef = useRef(null);
  const [addrSugs, setAddrSugs] = useState(null);
  const [showAddrSugs, setShowAddrSugs] = useState(false);
  const [addrSelected, setAddrSelected] = useState(false);
  const [addrError, setAddrError] = useState(false);
  const addrTypingRef = useRef(false);
  const addrAcRef = useRef(null);
  const office = officeFor(form.country);

  useEffect(() => {
    if (!addrTypingRef.current) return;
    const q = form.address.trim();
    if (q.length < 3) {
      setAddrSugs(null);
      setShowAddrSugs(false);
      return;
    }
    const timer = setTimeout(() => {
      loadGoogleMaps((i18n.language || "en").slice(0, 2))
        .then((google) => {
          if (!addrAcRef.current) addrAcRef.current = new google.maps.places.AutocompleteService();
          addrAcRef.current.getPlacePredictions({ input: q }, (preds, status) => {
            if (!addrTypingRef.current) return;
            setAddrSugs(status === "OK" && preds ? preds.slice(0, 5) : []);
            setShowAddrSugs(true);
          });
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [form.address, i18n.language]);

  const FAQS = [
    { q: t("rq.faq1q"), a: t("rq.faq1a") },
    { q: t("rq.faq2q"), a: t("rq.faq2a") },
    { q: t("rq.faq3q"), a: t("rq.faq3a") },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    const idx = uploadIdxRef.current;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/quotes/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParts((ps) => ps.map((p, j) => (j === idx ? { ...p, photo: r.data.url } : p)));
      toast.success(t("rq.photoAttached"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("rq.uploadFailed"),
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t("rq.errNameEmail"));
      return;
    }
    if (!addrSelected) {
      setAddrError(true);
      toast.error(t("forms.addressSelectError"));
      document.getElementById("rq-address")?.focus();
      return;
    }
    const anyPart = parts.some(
      (p) => p.brand.trim() || p.reference.trim() || p.description.trim() || p.model.trim(),
    );
    if (!anyPart) {
      toast.error(t("rq.errTellUs"));
      return;
    }
    setSubmitting(true);
    try {
      const filledParts = parts
        .map((p) => ({
          brand: p.brand.trim(),
          model: p.model.trim(),
          reference: p.reference.trim(),
          reference_brand: p.reference_brand.trim(),
          serial: p.serial.trim(),
          qty: p.qty.trim(),
          description: p.description.trim(),
          photo: p.photo,
        }))
        .filter((p) => Object.values(p).some(Boolean));
      await api.post("/quotes", {
        ...form,
        lang: (i18n.language || "en").slice(0, 2),
        phone: withDial(form.phone, form.country),
        parts: filledParts,
        machine_brand: filledParts[0]?.brand || "",
        machine_model: filledParts[0]?.model || "",
        serial_number: filledParts[0]?.serial || "",
        part_numbers: filledParts.map((p) => p.reference).filter(Boolean).join(", "),
        quantity: filledParts.map((p) => p.qty).filter(Boolean).join(", "),
        inquiry_type: "general",
        message: filledParts.map((p) => p.description).filter(Boolean).join("\n"),
        photo_url: filledParts.map((p) => p.photo).find(Boolean) || "",
        routed_office: office.company,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("rq.errGeneric"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const crumbs = [{ name: t("qa2.home"), path: "/" }, { name: t("rq.crumb") }];

  return (
    <Layout>
      <SEO
        title={t("rq.title")}
        description={t("rq.desc")}
        path="/request-quote"
        jsonLd={[
          breadcrumbJsonLd(
            crumbs.map((c) => ({
              name: c.name,
              path: c.path || "/request-quote",
            })),
          ),
          faqJsonLd(FAQS),
        ]}
      />
      <Breadcrumbs items={crumbs} />
      {done ? (
        <div
          className="p-6 sm:p-14 bg-white border-b border-border"
          data-testid="quote-success"
        >
          <CheckCircle2 size={40} className="text-yellow-600 mb-4" />
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tighter text-gray-950 mb-3">
            {t("rq.doneTitle")}
          </h1>
          <p className="text-gray-600 max-w-2xl mb-6">
            {t("rq.doneText", { name: form.name, email: form.email })}
          </p>
          <Link
            to={lp("/search")}
            className="inline-block bg-[#0A0A0A] text-yellow-500 font-mono-tech text-xs uppercase tracking-[0.2em] px-6 py-3 hover:bg-yellow-500 hover:text-black transition-colors"
          >
            {t("rq.browseCatalog")}
          </Link>
        </div>
      ) : (
        <>
          <div className="p-6 sm:p-14 border-b border-border bg-white">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
              {t("rq.kicker")}
            </p>
            <h1
              className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tighter mb-4 text-gray-950"
              data-testid="request-quote-title"
            >
              {t("rq.h1a")}{" "}
              {t("rq.h1b")}
            </h1>
            <p className="text-gray-600 max-w-3xl">{t("rq.heroSub")}</p>
          </div>
          <div className="bg-white border-b border-border" data-testid="rq-intro-choice">
            <div className="px-6 sm:px-14 pt-10 pb-6">
              <p className="font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl text-gray-950 max-w-2xl leading-tight">
                {t("rq2.introTitle")}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 px-6 sm:px-14 pb-12 gap-5">
              <Link
                to={lp("/brands")}
                data-testid="rq-choice-catalog"
                className="group relative overflow-hidden flex flex-col bg-[#0A0A0A] p-8 sm:p-10"
              >
                <img
                  src="/components/catalog-bg.webp"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover object-right opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
                />
                <span className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-950/60 to-gray-950/20" aria-hidden="true" />
                <span className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-gray-950/90 to-transparent" aria-hidden="true" />
                <span className="relative font-mono-tech text-[10px] tracking-[0.25em] text-gray-400">01</span>
                <span className="relative mt-4 font-display font-black uppercase tracking-tighter text-2xl text-white leading-none">
                  {t("rq2.catalogT")}
                </span>
                <span className="relative mt-3 text-sm text-gray-300 leading-relaxed max-w-sm">{t("rq2.catalogD")}</span>
                <span className="relative mt-8 font-display font-black text-2xl text-yellow-500 transition-transform duration-200 group-hover:translate-x-2">→</span>
              </Link>
              <button
                type="button"
                data-testid="rq-choice-direct"
                onClick={() => {
                  setShowForm(true);
                  window.requestAnimationFrame(() =>
                    document.getElementById("rq-form")?.scrollIntoView({ behavior: "smooth", block: "start" }),
                  );
                }}
                className="group flex flex-col bg-yellow-500 p-8 sm:p-10 text-left transition-colors hover:bg-yellow-400"
              >
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-black/50">02</span>
                <span
                  data-testid="rq-direct-eyebrow"
                  className="mt-4 self-start bg-[#0A0A0A] text-yellow-500 font-mono-tech font-bold text-[11px] uppercase tracking-[0.12em] px-3 py-2 leading-snug"
                >
                  {t("rq2.directEyebrow")}
                </span>
                <span className="mt-3 font-display font-black uppercase tracking-tighter text-2xl text-black leading-none">
                  {t("rq2.directT")}
                </span>
                <span className="mt-3 text-sm text-black/70 leading-relaxed max-w-sm">{t("rq2.directD")}</span>
                <span className="mt-8 font-display font-black text-2xl text-black transition-transform duration-200 group-hover:translate-y-1">↓</span>
              </button>
            </div>
          </div>
          {showForm && (
          <form
            id="rq-form"
            onSubmit={submit}
            className="bg-white border-b border-border p-6 sm:p-14"
            data-testid="request-quote-form"
          >
            <div className="max-w-3xl grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{t("rq.lName")}</label>
                <input
                  data-testid="rq-name"
                  className={inputCls}
                  value={form.name}
                  onChange={set("name")}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>{t("rq.lCompany")}</label>
                <input
                  data-testid="rq-company"
                  className={inputCls}
                  value={form.company}
                  onChange={set("company")}
                />
              </div>
              <div>
                <label className={labelCls}>{t("rq.lEmail")}</label>
                <input
                  data-testid="rq-email"
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>{t("rq.lCountry")}</label>
                <CountrySelect
                  testid="rq-country"
                  required
                  value={form.country}
                  onChange={(v) => setForm((f) => ({ ...f, country: v }))}
                  options={COUNTRIES}
                  placeholder={t("rq.selectCountry")}
                  className={inputCls}
                />
              </div>
              <div className="relative sm:col-span-2">
                <label className={labelCls} htmlFor="rq-address">
                  {t("contact2.fAddress")} *
                </label>
                <input
                  id="rq-address"
                  data-testid="rq-address"
                  className={inputCls}
                  value={form.address}
                  autoComplete="off"
                  required
                  onChange={(e) => {
                    addrTypingRef.current = true;
                    setAddrSelected(false);
                    if (!e.target.value.trim()) setAddrError(false);
                    setForm((f) => ({ ...f, address: e.target.value }));
                  }}
                  onBlur={() =>
                    setTimeout(() => {
                      setShowAddrSugs(false);
                      addrTypingRef.current = false;
                      if (!addrSelected && form.address.trim()) setAddrError(true);
                    }, 150)
                  }
                />
                {showAddrSugs && addrSugs && addrSugs.length > 0 && (
                  <div
                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-border z-30 shadow-[0_12px_24px_rgba(0,0,0,0.12)]"
                    data-testid="rq-address-suggestions"
                  >
                    {addrSugs.map((m, i) => (
                      <button
                        key={m.place_id || i}
                        type="button"
                        data-testid={`rq-address-option-${i}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setForm((f) => ({ ...f, address: m.description }));
                          setAddrSelected(true);
                          setAddrError(false);
                          setShowAddrSugs(false);
                          setAddrSugs(null);
                          addrTypingRef.current = false;
                        }}
                        className="w-full text-left px-4 py-2.5 text-xs border-b border-border last:border-b-0 hover:bg-gray-50 transition-colors"
                      >
                        {m.description}
                      </button>
                    ))}
                  </div>
                )}
                {addrError && (
                  <p className="text-xs text-red-600 mt-1.5" data-testid="rq-address-error">
                    {t("forms.addressSelectError")}
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>{t("rq.lPhone")}</label>
                <div className="flex">
                  {dialFor(form.country) && (
                    <span
                      data-testid="rq-dial-code"
                      className="flex items-center border border-r-0 border-input bg-gray-100 px-2.5 font-mono-tech text-xs text-gray-700 shrink-0"
                    >
                      {dialFor(form.country)}
                    </span>
                  )}
                  <input
                    data-testid="rq-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </div>
              </div>
              <div className="sm:col-span-2" data-testid="rq-parts-section">
                <div className="flex items-center justify-between border-b border-border pb-2 mb-4">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-yellow-600">{t("rq3.partsTitle")}</p>
                </div>
                <div className="space-y-4">
                  {parts.map((p, i) => (
                    <div key={i} className="relative border border-border border-l-4 border-l-yellow-500 bg-gray-50/60 p-4 sm:p-5" data-testid={`rq-part-row-${i}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono-tech text-[10px] tracking-[0.2em] text-gray-400">
                          {t("rq3.part").toUpperCase()} 0{i + 1}
                        </span>
                        {parts.length > 1 && (
                          <button
                            type="button"
                            data-testid={`rq-part-remove-${i}`}
                            onClick={() => removePart(i)}
                            aria-label={`Remove part ${i + 1}`}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <X size={15} />
                          </button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>{t("rq.lBrand")}</label>
                          <input data-testid={`rq-part-brand-${i}`} className={inputCls} placeholder={t("rq.phBrand")} value={p.brand} onChange={setPart(i, "brand")} />
                        </div>
                        <div>
                          <label className={labelCls}>{t("rq.lModel")}</label>
                          <input data-testid={`rq-part-model-${i}`} className={inputCls} placeholder={t("rq.phModel")} value={p.model} onChange={setPart(i, "model")} />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className={labelCls}>{t("rq3.lRefBrand")}</label>
                          <input data-testid={`rq-part-reference-brand-${i}`} className={inputCls} placeholder={t("rq3.phRefBrand")} value={p.reference_brand} onChange={setPart(i, "reference_brand")} />
                        </div>
                        <div>
                          <label className={labelCls}>{t("rq3.lRef")}</label>
                          <input data-testid={`rq-part-reference-${i}`} className={inputCls} placeholder={t("rq.phPartNums")} value={p.reference} onChange={setPart(i, "reference")} />
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1.5" data-testid={`rq-part-reference-hint-${i}`}>{t("rq3.refHint")}</p>
                      <div className="grid grid-cols-[1fr_88px] gap-4 mt-4">
                        <div>
                          <label className={labelCls}>{t("rq.lSerial")}</label>
                          <input data-testid={`rq-part-serial-${i}`} className={inputCls} placeholder={t("rq.phSerial")} value={p.serial} onChange={setPart(i, "serial")} />
                        </div>
                        <div>
                          <label className={labelCls}>{t("rq.lQty")}</label>
                          <input data-testid={`rq-part-qty-${i}`} className={inputCls} inputMode="numeric" placeholder="1" value={p.qty} onChange={setPart(i, "qty")} />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className={labelCls}>{t("rq3.lDesc")}</label>
                        <textarea
                          data-testid={`rq-part-description-${i}`}
                          rows={2}
                          className={inputCls}
                          placeholder={t("rq.phWhichParts")}
                          value={p.description}
                          onChange={setPart(i, "description")}
                        />
                      </div>
                      <div className="mt-3">
                        {!p.photo ? (
                          <button
                            type="button"
                            data-testid={`rq-part-photo-button-${i}`}
                            onClick={() => {
                              uploadIdxRef.current = i;
                              fileRef.current?.click();
                            }}
                            disabled={uploading}
                            className="flex items-center gap-2 border border-dashed border-gray-400 px-3.5 py-2 text-xs text-gray-600 hover:border-gray-950 hover:text-gray-950 transition-colors disabled:opacity-50"
                          >
                            <Camera size={14} />
                            {uploading && uploadIdxRef.current === i ? t("rq.uploadingBtn") : t("rq.attach")}
                          </button>
                        ) : (
                          <div className="flex items-center gap-3" data-testid={`rq-part-photo-attached-${i}`}>
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${p.photo}`}
                              alt={t("imgAlt.attachedPart")}
                              className="h-14 w-14 object-cover border border-border"
                            />
                            <button
                              type="button"
                              onClick={() => setParts((ps) => ps.map((x, j) => (j === i ? { ...x, photo: "" } : x)))}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                            >
                              <X size={13} /> {t("rq.remove")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="rq-add-part"
                  onClick={addPart}
                  className="mt-4 w-full border-2 border-dashed border-gray-300 py-3 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 hover:border-yellow-500 hover:text-gray-950 hover:bg-yellow-500/5 transition-colors"
                >
                  + {t("rq3.addPart")}
                </button>
              </div>
              <div className="sm:col-span-2 hidden">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                />
              </div>
              {form.country && (
                <div
                  data-testid="rq-company-address"
                  className="sm:col-span-2 border-l-2 border-yellow-500 border-t border-r border-b border-border bg-gray-50 p-4"
                >
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">
                    {t("rq.handledBy")}
                  </p>
                  <p className="text-sm font-semibold text-gray-950" data-testid="rq-office-company">
                    {office.company}
                  </p>
                  <p className="text-xs text-gray-500">{office.subtitle || office.label}</p>
                  <p className="text-sm text-gray-600 mt-1">{office.address}</p>
                  {(office.phone || office.email) && (
                    <p className="text-sm text-gray-600 font-mono-tech mt-0.5">
                      {[office.phone, office.email].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <PrivacyConsent id="rq-privacy-consent" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="rq-submit"
                  disabled={submitting || uploading}
                  className="bg-yellow-500 text-black font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#0A0A0A] hover:text-yellow-500 transition-colors disabled:opacity-50"
                >
                  {submitting ? t("rq.sending") : t("rq.submit")}
                </button>
                <p className="text-xs text-gray-400 mt-3">{t("rq.note")}</p>
              </div>
            </div>
          </form>
          )}
          <section className="bg-white border-b border-border p-6 sm:p-14">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
              FAQ
            </p>
            <div className="max-w-3xl divide-y divide-border">
              {FAQS.map((f) => (
                <details key={f.q} className="py-4 group">
                  <summary className="cursor-pointer font-semibold text-gray-950 list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-yellow-600 font-mono-tech ml-4 group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="text-sm text-gray-600 mt-2">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        </>
      )}
    </Layout>
  );
}
